/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface RegionGroupProps {
  id: string;
  name: string;
  abbreviation: string;
  departmentId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class RegionGroupRecord extends Record<RegionGroupProps>({
  id: null as any,
  name: null as any,
  abbreviation: null as any,
  departmentId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    abbreviation: string;
    departmentId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): RegionGroupRecord {
    if (json == null) {
      throw new Error('RegionGroupRecord.fromJS: parameter cannot be null');
    }
    return new RegionGroupRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'RegionGroupJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'RegionGroupJson.name'),
      abbreviation: getFromOrThrow(json, (j) => j.abbreviation, 'RegionGroupJson.abbreviation'),
      departmentId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'departmentId', '00000000-0000-0000-0000-000000000000'),
        'RegionGroupJson.departmentId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'RegionGroupJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    abbreviation: string;
    departmentId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [RegionGroupRecord, Error[]] {
    if (json == null) {
      throw new Error('RegionGroupRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new RegionGroupRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'RegionGroupJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'RegionGroupJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      abbreviation: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.abbreviation, 'RegionGroupJson.abbreviation'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      departmentId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'departmentId', '00000000-0000-0000-0000-000000000000'),
            'RegionGroupJson.departmentId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'RegionGroupJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: RegionGroupProps) {
    super(props);
    if (props == null) {
      throw new Error('RegionGroupRecord constructor parameter cannot be null');
    }
  }
}

export abstract class RegionGroupViewModelBase implements RegionGroupProps {
  protected readonly _props: RegionGroupProps;
  private _json?: RegionGroupProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get abbreviation(): string {
    return this._props.abbreviation;
  }
  get departmentId(): string {
    return this._props.departmentId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: RegionGroupProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        abbreviation: this._props.abbreviation,
        departmentId: this._props.departmentId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllRegionGroupEntities = (state: AppState) =>
  state.entityStores.regionGroup.entities;

export const selectRegionGroupById = (id: string) => (state: AppState) =>
  state.entityStores.regionGroup.entities.get(id);

export const selectRegionGroupMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.regionGroup.meta.get(id);

export const selectRegionGroupAndMetadataById = (id: string) =>
  createSelector(
    selectRegionGroupById(id),
    selectRegionGroupMetadataById(id),
    (regionGroup, meta) => ({ regionGroup, meta }),
  );

export function selectRegionGroupEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllRegionGroupEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, RegionGroupRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectRegionGroupEntitiesByName(name: string) {
  return createSelector(selectAllRegionGroupEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectRegionGroupEntitiesByAbbreviation(abbreviation: string) {
  return createSelector(selectAllRegionGroupEntities, (entities) =>
    entities.filter((e) => e.abbreviation === abbreviation).valueSeq(),
  );
}

export function selectRegionGroupEntitiesByDepartmentId(departmentId: string) {
  return createSelector(selectAllRegionGroupEntities, (entities) =>
    entities.filter((e) => e.departmentId === departmentId).valueSeq(),
  );
}

export function selectRegionGroupEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllRegionGroupEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
